import { useMemo } from "react";
import { useClosingAlerts } from "../hooks/useClosingAlerts";
import { AlertTriangle, Clock, Wifi, WifiOff } from "lucide-react";

/**
 * Urgency tiers (in seconds):
 *   < 15m  → red, pulsing (CRÍTICO)
 *   < 30m  → orange
 *   < 60m  → yellow
 *   < 120m → slate (informative)
 */
function urgencyOf(sec) {
  if (sec < 15 * 60) return "critical";
  if (sec < 30 * 60) return "high";
  if (sec < 60 * 60) return "medium";
  return "low";
}

const STYLE = {
  critical: "bg-red-600 text-white border-red-700 animate-pulse",
  high: "bg-orange-500 text-white border-orange-600",
  medium: "bg-yellow-100 text-yellow-900 border-yellow-300",
  low: "bg-slate-50 text-slate-700 border-slate-200",
};

function formatLeft(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m >= 60) {
    const h = Math.floor(m / 60);
    const rem = m % 60;
    return `${h}h ${String(rem).padStart(2, "0")}m`;
  }
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function ClosingCountdownBanner({ compact = false }) {
  const { alerts, connected } = useClosingAlerts();

  const critical = useMemo(() => alerts.find((a) => urgencyOf(a.seconds_left) === "critical"), [alerts]);
  const visible = compact ? alerts.slice(0, 3) : alerts;

  if (alerts.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-sm px-4 py-3 flex items-center gap-2 text-xs text-slate-500" data-testid="closing-banner-empty">
        <Clock className="w-3 h-3" />
        Sin cierres inminentes en las próximas 2 horas.
        <span className="ml-auto flex items-center gap-1 font-mono">
          {connected ? (
            <><Wifi className="w-3 h-3 text-green-600" /><span className="text-green-700">en vivo</span></>
          ) : (
            <><WifiOff className="w-3 h-3 text-slate-400" /><span>reconectando…</span></>
          )}
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="closing-banner">
      {critical && !compact && (
        <div className={`border-2 ${STYLE.critical} rounded-sm px-5 py-3 flex items-center gap-3`} data-testid="closing-banner-critical">
          <AlertTriangle className="w-5 h-5 shrink-0" />
          <div className="flex-1">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-80">Cierre crítico</div>
            <div className="font-black text-lg tracking-tight" style={{ fontFamily: "Manrope" }}>
              {critical.game_name} cierra en {formatLeft(critical.seconds_left)}
            </div>
          </div>
          <span className="text-3xl font-black font-mono tabular-nums">{formatLeft(critical.seconds_left)}</span>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="px-4 py-2 border-b border-slate-200 flex items-center justify-between bg-slate-50">
          <div className="flex items-center gap-2">
            <Clock className="w-3 h-3 text-slate-500" />
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-600">
              Próximos cierres ({alerts.length})
            </span>
          </div>
          <span className="text-[10px] font-mono flex items-center gap-1">
            {connected ? (
              <><span className="w-1.5 h-1.5 bg-green-600 rounded-full pulse-dot" /><span className="text-green-700">vivo</span></>
            ) : (
              <><span className="w-1.5 h-1.5 bg-slate-400 rounded-full" /><span className="text-slate-500">reconectando…</span></>
            )}
          </span>
        </div>
        <ul className="divide-y divide-slate-100">
          {visible.map((a) => {
            const urg = urgencyOf(a.seconds_left);
            return (
              <li key={a.game_id} className="px-4 py-2 flex items-center justify-between gap-3 hover:bg-slate-50" data-testid={`closing-row-${a.game_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900 truncate">{a.game_name}</div>
                  <div className="text-[10px] text-slate-500 font-mono uppercase tracking-wider">Cierre {a.closing_time}</div>
                </div>
                <span
                  className={`text-xs font-black font-mono tabular-nums px-3 py-1 rounded-sm border ${STYLE[urg]}`}
                  data-testid={`closing-countdown-${a.game_id}`}
                >
                  {formatLeft(a.seconds_left)}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
